
import { MatchingModel } from '@/Domains/Matching';
import { PropertyMediaFileModel, PropertyModel } from "../Models";
import {
    DateFormatHelper,
    SoNumber,
    DetailItem,
    MediaItem,
    MediaType,
    DetailList,
    DetailSection,
    PropertyEnums,
    AppEnumHelper,
    dateCompare,
    DOMAIN_TYPES,
    RecordMap
} from "@/Shared";
import { AuditViewModel } from '@/Shared/Logic/ViewModels/AuditViewModel';
import { AuditModel } from '@/Shared/Logic/Models/AuditModel';
import { DocumentModel } from '@/Domains/Document';
import { AccessRightModel } from '@/Domains/Auth';
import { Row } from '@/Shared/UI/Components/DetailsListBox/DetailsListBox';

const BACK_URL = process.env.REACT_APP_AMAT_API_URL;
export interface PropertyVMParams {
    property: PropertyModel;
    viewEnums: PropertyEnums;
    documentMap?: RecordMap<DocumentModel>;
    accessRight: AccessRightModel;
    isDisplaying: boolean;
}


export class PropertyViewModel {

    public id: number;
    public model!: PropertyModel;

    public operationType: string;
    public propertyCategory: string;

    /** label of property */
    public propertyType!: string;
    public sector!: string;
    public sectorId!: number;
    public city!: string;
    public cityId!: number;
    public availability!: string;
    public availableAt!: string;
    public price: string;
    public totalPrice: string;
    public mandateId?: string;
    public furnished: string;
    public condition: string;
    public yearBuilt: number;
    public address: string;
    public amenityFeatures: string[];
    public serviceFeatures: string[];

    public surfaceArea: string;
    public roomCount: string;

    public standing: string;
    private priceRangeLabel: string;

    public serviceChargeProvision: string;
    public acMaintenanceCharge: string;
    public securityDeposit: string;

    public matchings: MatchingModel[] = [];
    public createdAt!: string;

    public propertyUsageType!: string;

    public ads!: {
        id: string,
        postedOn: string,
        postedAt: string,
        note: string
    }[];

    public cardHeaderTitle!: string;
    public cardClass!: string;
    public isDeleted!: boolean;

    public description!: string;

    public audit!: AuditViewModel;
    private _documentMap?: RecordMap<DocumentModel>;

    public nonDeletedMedias: PropertyMediaFileModel[];

    public constructor(private params: PropertyVMParams) {

        const { viewEnums, property, documentMap, isDisplaying } = this.params;


        this._documentMap = documentMap;
        this.model = property;

        this.id = property?.id || 0;

        this.audit = new AuditViewModel(property as AuditModel);

        this.operationType = AppEnumHelper.getLabel(viewEnums.OperationTypeEnum, property.operationType);

        this.standing = AppEnumHelper.getLabel(viewEnums.StandingEnum, property.standing);

        this.condition = AppEnumHelper.getLabel(viewEnums.PropertyConditionEnum, property.condition);

        this.availability = AppEnumHelper.getLabel(viewEnums.AvailabilityEnum, property.availability);

        this.propertyType = AppEnumHelper.getLabel(viewEnums.PropertyTypeEnum, property.propertyType);

        this.propertyCategory = AppEnumHelper.getLabel(viewEnums.PropertyCategoryEnum, property.propertyCategory);

        this.matchings = this.model.matchings || [];

        this.amenityFeatures = (this.model.amenityFeatures ?? []).map(af => AppEnumHelper.getLabel(viewEnums.PropertyFeatureTypeEnum, af));

        this.serviceFeatures = (this.model.serviceFeatures ?? []).map(sf => AppEnumHelper.getLabel(viewEnums.PropertyFeatureTypeEnum, sf));

        this.price = SoNumber.toFR(this.model?.price);

        this.sectorId = this.model?.sectorId ?? 0;

        this.sector = this.model?.sector?.name || "";

        this.cityId = this.model?.sector?.city?.id ?? 0;
        this.city = this.model?.sector?.city?.name || "";

        this.availableAt = DateFormatHelper.toLocaleDateString(this.model?.availableAt) || "";


        this.furnished = this.model.furnished ? "Oui" : "Non";

        this.description = this.model?.description ?? "";


        this.yearBuilt = this.model.yearBuilt || 0;

        this.address = this.model.address || "";

        this.surfaceArea = SoNumber.toFR(this.model.surfaceArea);

        this.roomCount = this.model.roomCount ? String(this.model.roomCount) : "";

        this.serviceChargeProvision = SoNumber.toFR(this.model.serviceChargeProvision);

        this.acMaintenanceCharge = SoNumber.toFR(this.model.acMaintenanceCharge);

        this.securityDeposit = SoNumber.toFR(this.model.securityDeposit);

        this.priceRangeLabel = AppEnumHelper.getLabel(viewEnums.PriceRangeEnum, this.model.priceRange);

        this.createdAt = DateFormatHelper.toLocaleString(this.model.createdAt) || "";

        this.totalPrice = SoNumber.toFR((this.model?.price || 0) + (this.model.serviceChargeProvision || 0));

        this.initPublications();

        this.cardHeaderTitle = this.propertyCategory + ' - ' + this.propertyType;
        this.isDeleted = !!property.deletedAt
        this.cardClass = isDisplaying ? 'active' : '';

        this.nonDeletedMedias = (this.model?.propertyMediaFiles ?? []).filter(pm => !pm.deletedAt);

        this.propertyUsageType = AppEnumHelper.getLabel(viewEnums.PropertyUsageEnum, property?.usageType);
    }

    hasDocument(): boolean {

        return this._documentMap ? !this._documentMap.isEmpty() : false;
    }

    hasListingSheet(): boolean {

        return this._documentMap ? !this._documentMap.isEmpty() : false;
    }

    hasRentalComitment(): boolean {

        return this._documentMap ? !this._documentMap.isEmpty() : false;
    }

    hasRoutineMaintenance(): boolean {

        return this._documentMap ? !this._documentMap.isEmpty() : false;
    }

    hasViewingAcknowledgment(): boolean {
        if(this._documentMap) {
            this._documentMap.map((data) => data);
        }

        return this._documentMap ? !this._documentMap.isEmpty() : false;
    }

    documentMap() : RecordMap<DocumentModel>|undefined {
        return this._documentMap;
    }


    /**
     * Returns the most recently added document, or null if none exist.
     */
    getLatestDocument() {
        return this._documentMap?.lastById() ?? null;
    }


    private initPublications() {
        const viewAds = [] as {
            id: string,
            postedOn: string,
            postedAt: string,
            note: string
        }[];
        (this.model?.propertyAds ?? []).forEach(propertyAd => {
            const ad = {
                id: String(propertyAd.id),
                postedOn: propertyAd?.adSourceType || "",
                postedAt: DateFormatHelper.toLocaleString(propertyAd.firstPostedAt) || "",
                note: propertyAd.note || "",
            }
            viewAds.push(ad);
        });
        this.ads = viewAds;
    }


    get title() {
        return "Bien N° " + this.id + " - " + this.propertyCategory;
    }

    getAllSections = (): DetailSection[] => {
        const sections: DetailSection[] = [
            {
                sectionFn: this.getGlobalSection,
                legend: 'Caractéristiques Générales',
                type: 'box'
            },
            {
                sectionFn: this.getLocalisationSection,
                legend: 'Adresse / Secteur',
                type: 'box'
            },
            {
                sectionFn: this.getRoomsSection,
                legend: 'Pièces',
                type: 'box'
            },
            {
                sectionFn: this.getPrestationsSection,
                legend: 'Commodité & Prestations',
                type: 'box'
            },
            {
                sectionFn: this.getFinancialSection,
                legend: 'éléments financiers',
                type: 'box'
            },
            {
                sectionFn: this.getOtherSection,
                legend: 'Autres',
                type: 'box'
            },
            {
                sectionFn: this.getCommercialHistorySection,
                legend: 'Historique Commercialisation',
                type: 'list'
            },
            {
                sectionFn: this.getMatchingSection,
                legend: 'Rapprochements',
                type: 'list'
            },
            {
                sectionFn: this.getPostsSection,
                legend: 'Publications',
                type: 'list'
            },
        ];

        if (this.params.accessRight.canAudit(DOMAIN_TYPES.Property)) {
            sections.push(
                {
                    sectionFn: this.audit.getAuditTrail,
                    legend: 'Traçabilité',
                    type: 'box'
                }
            );
        }
        return sections;
    }

    getGlobalSection = () => {
        return [
            { label: "Bien N°", value: this.id },
            { label: "Nature de bien", value: this.propertyCategory },
            { label: "Meublé", value: this.furnished },
            { label: "Disponibilité", value: this.availability },
            { label: "Type de bien", value: this.propertyType },
            { label: "Usage", value: this.propertyUsageType },
            { label: "Disponible le", value: this.availableAt },
            { label: "état général", value: this.condition },
            { label: "année de construction", value: this.yearBuilt },

        ] as DetailItem[];
    }

    getLocalisationSection = () => {
        return [
            { label: "Adresse", value: this.address },
            { label: "Secteur", value: this.sector },
            { label: "Ville", value: this.city },

        ] as DetailItem[];

    }

    getRoomsSection = () => {
        return [
            { label: "Nombre de pièces", value: this.roomCount },
            { label: "Surface Terrain", value: this.surfaceArea },
            { label: "Détails des pièces", value: this.amenityFeatures.join(', ') },

        ] as DetailItem[];

    }

    getPrestationsSection = () => {
        return [
            { label: "Prestations", value: this.serviceFeatures.join(', ') },
            { label: "Standing", value: this.standing },
        ] as DetailItem[];

    }

    getFinancialSection = () => {
        return [
            { label: "Opération", value: this.operationType },
            { label: "Prix/Loyer Hc", value: this.price },
            { label: "Provisions sur charges", value: this.serviceChargeProvision },
            { label: "Entretien Climatisation", value: this.acMaintenanceCharge },
            { label: "Dépôt de garantie", value: this.securityDeposit },
            { label: "Prix/Loyer Total", value: this.totalPrice },
            { label: "Fourchette prix", value: this.priceRangeLabel },
        ] as DetailItem[];

    }

    getOtherSection = () => {
        return [
            { label: "Descriptifs détaillés", value: this.description }
        ];
    }

    getCommercialHistorySection = (): DetailList => {
        const headers = ["Question pour Armand"];
        const rows = [{
            items: [{ label: "Je ne sais pas ce que c'est ????" }]
        }];
        return { headers, rows };
    }

    getMatchingSection = (): DetailList => {
        const headers = ["N° Appr.", "N° Besoin", "Status", "Date"];

        const rows = [] as Row[];
        this.matchings.forEach(matching =>
            rows.push(
                {
                    items: [
                        { label: String(matching.id) },
                        { label: String(matching.needId) },
                        { label: AppEnumHelper.getLabel(this.params.viewEnums.MatchingStatusEnum, matching.status) },
                        { label: DateFormatHelper.toLocaleString(matching.createdAt) || "" }
                    ]
                }));

        return { headers, rows };
    }


    getPostsSection = (): DetailList => {
        const headers = ["N° Publication", "Publié le", "Publié sur", "Note"];
        const rows = this.ads.map(ad => {
            return {
                items: [
                    { label: ad.id },
                    { label: ad.postedAt },
                    { label: ad.postedOn },
                    { label: ad.note }
                ]
            }
        });
        return { headers, rows };
    }

    hasImage = () => (this.nonDeletedMedias ?? []).some(media => media.largeMedia?.fileType === "image");
    hasVideo = () => (this.nonDeletedMedias ?? []).some(media => media.largeMedia?.fileType === "video")

    getMediaItems = (): { images: MediaItem[], videos: MediaItem[] } => {

        const images = [] as MediaItem[];
        const videos = [] as MediaItem[];

        this.nonDeletedMedias.forEach(pm => {
            const isImage = pm.largeMedia?.fileType === 'image';

            const basePath = isImage ? BACK_URL + '/storage/' : '';
            const media = {
                type: pm.largeMedia?.fileType as MediaType,
                large: basePath + pm.largeMedia?.filePath,
                small: basePath + pm.smallMedia?.filePath,
                id: pm.id,
                caption: pm.caption
            } as MediaItem;

            isImage ? images.push(media) : videos.push(media);
        });

        return { images, videos };
    };

    compareTo(other: PropertyViewModel): number {
        return dateCompare(this.model.createdAt ?? "", other.model.createdAt ?? "");
    }
}